import { AppManagementArea } from 'paths/link'
import { createContext, useContext } from 'react'

export type AppManagementContextProps = {
  /**
   * Preserved to minimize the SDK decouple footprint. Could later surface
   * async failures (e.g. the RPC flow under AppManagementContextProvider).
   */
  error?: Error | null
  /** App ID currently in context.*/
  appId: string
  /** True if the app is a dedicated cluster. */
  isDedicatedCluster: boolean
  /** True if the domain status is ready since the first moment the status is queried. */
  isDomainInitiallyReady: boolean
  /** True if the ingress domain for the app is ready. */
  isDomainReady: boolean
  /** True if the ingress domain for the app is in error state. */
  isDomainInError: boolean
  /** Number of times the domain status has been queried. */
  queryAttempts: number
  /** True if the streams feature is available to the user */
  allowStreams: boolean
  /** True if the viewer can update the app */
  viewerCanUpdateApp: boolean
  /** True if the viewer can access metrics */
  viewerCanAccessMetrics: boolean
  /** True if the viewer can read app data */
  viewerCanReadAppData: boolean
  /** True if the viewer can write app data */
  viewerCanWriteAppData: boolean
  /** True if the viewer can read offline licenses */
  viewerCanReadLicense: boolean
  /** True if the viewer can read API keys */
  viewerCanReadApiKey: boolean
  /**
   * Staff-override data gates. These bake in the `|| isSuperAdmin`
   * override so nav and every router share one policy instead of re-deriving
   * it (and drifting). Derived once in AppManagementScene. Optional here so
   * tests and non-management consumers can provide just the gates they need.
   */
  allowCollections?: boolean
  allowDevices?: boolean
  allowAuth?: boolean
  allowAccess?: boolean
  allowAuthentication?: boolean
  /**
   * The default area to land on when the current URL has no area match.
   * Derived in AppManagementScene from the data gates. Optional so tests and
   * non-management consumers need not supply it; the router falls back to
   * 'settings' when absent.
   */
  defaultArea?: AppManagementArea
  /** True if the viewer can access mongo connector screen(s) */
  viewerCanAccessMongo: boolean
  /** The cloud domain URL for the app */
  cloudDomainUrl?: string
  /** Whether websocket connections are enabled for the app */
  wsEnabled?: boolean
  /** Whether HTTP connections are enabled for the app */
  httpEnabled?: boolean
}

const AppSettingsContext = createContext<AppManagementContextProps>({
  appId: '',
  isDomainInitiallyReady: false,
  isDedicatedCluster: false,
  isDomainReady: false,
  isDomainInError: false,
  queryAttempts: 0,
  allowStreams: false,
  viewerCanUpdateApp: false,
  viewerCanAccessMetrics: false,
  viewerCanReadAppData: false,
  viewerCanWriteAppData: false,
  viewerCanReadLicense: false,
  viewerCanReadApiKey: false,
  viewerCanAccessMongo: false,
})

export const useAppManagementContext = () => useContext(AppSettingsContext)

export default AppSettingsContext
